import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Building2 } from "lucide-react";
import FAQSection from "@/components/FAQSection";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";

function useFaqData() {
  const { t } = useTranslation();
  return [
    {
      category: t("faq.forHomeowners"),
      iconName: "Home",
      color: "bg-blue-100 text-blue-600",
      items: [
        { q: t("faq.h_escrow_q"), a: t("faq.h_escrow_a") },
        { q: t("faq.h_release_q"), a: t("faq.h_release_a") },
        { q: t("faq.h_dispute_q"), a: t("faq.h_dispute_a") },
        { q: t("faq.h_verified_q"), a: t("faq.h_verified_a") },
        { q: t("faq.h_protected_q"), a: t("faq.h_protected_a") },
      ],
    },
    {
      category: t("faq.forContractors"),
      iconName: "HardHat",
      color: "bg-amber-100 text-amber-600",
      items: [
        { q: t("faq.c_paid_q"), a: t("faq.c_paid_a") },
        { q: t("faq.c_fee_q"), a: t("faq.c_fee_a") },
        { q: t("faq.c_verify_q"), a: t("faq.c_verify_a") },
        { q: t("faq.c_dispute_q"), a: t("faq.c_dispute_a") },
        { q: t("faq.c_stripe_q"), a: t("faq.c_stripe_a") },
      ],
    },
    {
      category: t("faq.general"),
      iconName: "HelpCircle",
      color: "bg-slate-100 text-slate-600",
      items: [
        { q: t("faq.g_city_q"), a: t("faq.g_city_a") },
        { q: t("faq.g_support_q"), a: t("faq.g_support_a") },
        { q: t("faq.g_services_q"), a: t("faq.g_services_a") },
      ],
    },
  ];
}

export default function FAQPage() {
  const { t } = useTranslation();
  const faqData = useFaqData();

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Navbar */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#0B1F3B] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <span className="font-bold text-[#0B1F3B]">{t("common.appName")}</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/login">
              <Button variant="ghost" size="sm">{t("common.logIn")}</Button>
            </Link>
            <Link to="/register">
              <Button size="sm" className="bg-[#0B1F3B] hover:bg-[#162A4C] text-white">{t("common.getStarted")}</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-[#0B1F3B] py-16">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
            {t("faq.title")}
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            {t("faq.subtitle")}
          </p>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        <FAQSection category={faqData[0].category} iconName={faqData[0].iconName} color={faqData[0].color} items={faqData[0].items} />
        <FAQSection category={faqData[1].category} iconName={faqData[1].iconName} color={faqData[1].color} items={faqData[1].items} />
        <FAQSection category={faqData[2].category} iconName={faqData[2].iconName} color={faqData[2].color} items={faqData[2].items} />

        {/* CTA */}
        <div className="text-center mt-12 p-8 bg-[#0B1F3B] rounded-2xl">
          <h3 className="text-xl font-bold text-white mb-2">{t("faq.stillQuestions")}</h3>
          <p className="text-slate-300 mb-6">{t("faq.postProject")}</p>
          <Link to="/register">
            <Button className="bg-[#D4AF37] hover:bg-[#C4A030] text-[#0B1F3B] font-bold px-8 h-12">
              {t("faq.getStartedFree")}
            </Button>
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-8">
        <div className="max-w-5xl mx-auto px-6 text-center text-sm text-slate-500">
          &copy; {new Date().getFullYear()} {t("common.appName")}. {t("landing.allRightsReserved")}
        </div>
      </footer>
    </div>
  );
}
